import { Button, TabsWithContent, useModal, useQueryState } from "@homebound/beam";
import { useParams } from "react-router";
import { Loading } from "src/components";
import {
  BidContractPlanPricing_RevisionFragment,
  useBidContractPlanPricing_RevisionQuery,
  useBidContractPlanPricing_ScopeLinesQuery,
} from "src/components/bid-contracts/BidContractPlanPricingPage.generated";
import { ImportBidModal } from "src/routes/bids/components/ImportBidModal";
import { ProrateBidView } from "src/routes/bids/components/ProrateBidModal";
import { queryResult } from "src/utils";

export function BidContractPlanPricingPage() {
  const { bidContractRevisionId } = useParams<{ bidContractRevisionId: string }>();
  const query = useBidContractPlanPricing_RevisionQuery({ variables: { bidContractRevisionId } });
  return queryResult(query, ({ bidContractRevision }) => (
    <BidContractPlanPricingView bidContractRevision={bidContractRevision} />
  ));
}

function BidContractPlanPricingView({
  bidContractRevision,
}: {
  bidContractRevision: BidContractPlanPricing_RevisionFragment;
}) {
  const { openModal } = useModal();
  const bidPackageVersion = bidContractRevision.bidPackageVersion!;
  const sortedRpavs = bidPackageVersion.readyPlanAggregateVersions.sortBy((rpav) => rpav.readyPlan.id) ?? [];
  const [selectedTab, setSelectedTab] = useQueryState<string>("planId", sortedRpavs[0].readyPlan.id);
  const query = useBidContractPlanPricing_ScopeLinesQuery({
    variables: { bidPackageVersionId: bidPackageVersion.id, filter: { readyPlan: [selectedTab] } },
  });
  return (
    <TabsWithContent
      right={
        bidContractRevision.bidContract.isInternalEstimate ? undefined : (
          <Button
            label="Upload Bid"
            variant="text"
            size="sm"
            disabled={!bidContractRevision.canEdit.allowed}
            onClick={() =>
              openModal({
                content: (
                  <ImportBidModal
                    requests={[bidContractRevision.bidContract.bidRequest!]}
                    bidPackageName={bidContractRevision.bidContract.name}
                    latestBidContractRevisions={[bidContractRevision]}
                    bidPackageVersionId={bidPackageVersion.id}
                    isUnitBased={false}
                    tradePartnerId={bidContractRevision.bidContract.tradePartner?.id}
                  />
                ),
              })
            }
          />
        )
      }
      alwaysShowAllTabs
      tabs={sortedRpavs.map((rpav) => ({
        name: rpav.displayName,
        value: rpav.readyPlan.id,
        render: () => {
          if (query.loading) return <Loading />;
          return queryResult(query, ({ bidPackageVersionScopeLines }) => (
            <ProrateBidView
              bidContractRevisionId={bidContractRevision.id}
              scopeLineItems={bidPackageVersionScopeLines.map((sli) => sli.takeoffLineItemVersion)}
              type={bidContractRevision.bidContract.type!.code}
              existingTradeLineItems={bidContractRevision.tradeLineItems}
              modalTreatment={false}
              disabled={!bidContractRevision.canEdit.allowed}
            />
          ));
        },
      }))}
      selected={selectedTab}
      onChange={setSelectedTab}
    />
  );
}
